import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Group, NumberInput, Stack, Switch, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import type { RestaurantZoneDto } from './types';
import { useCreateZone, useUpdateZone } from './floorPlanApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  branchId: string;
  zone: RestaurantZoneDto | null;
}

interface FormValues {
  code: string;
  nameAr: string;
  nameEn: string;
  sortOrder: number;
  isActive: boolean;
}

export function ZoneFormDrawer({ opened, onClose, branchId, zone }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateZone();
  const update = useUpdateZone();
  const submitting = create.isPending || update.isPending;
  const editing = zone !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: { code: '', nameAr: '', nameEn: '', sortOrder: 0, isActive: true },
    validate: {
      code: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      code: zone?.code ?? '',
      nameAr: zone?.nameAr ?? '',
      nameEn: zone?.nameEn ?? '',
      sortOrder: zone?.sortOrder ?? 0,
      isActive: zone?.isActive ?? true,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, zone]);

  const submit = async (values: FormValues) => {
    const payload = {
      code: values.code.trim(),
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      sortOrder: values.sortOrder,
    };
    try {
      if (editing) {
        await update.mutateAsync({
          id: zone.restaurantZoneId,
          request: { ...payload, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync({ branchId, ...payload });
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('floorPlan.zoneForm.editTitle') : t('floorPlan.zoneForm.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <TextInput
            label={t('floorPlan.zoneForm.codeLabel')}
            description={t('floorPlan.zoneForm.codeHelp')}
            placeholder="MAIN"
            withAsterisk
            {...form.getInputProps('code')}
          />
          <TextInput
            label={t('floorPlan.zoneForm.nameArLabel')}
            placeholder={t('floorPlan.zoneForm.nameArPlaceholder')}
            withAsterisk
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('floorPlan.zoneForm.nameEnLabel')}
            placeholder={t('floorPlan.zoneForm.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <NumberInput
            label={t('floorPlan.zoneForm.sortOrderLabel')}
            min={0}
            {...form.getInputProps('sortOrder')}
          />
          {editing && (
            <Switch
              label={t('floorPlan.zoneForm.activeLabel')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
